import { useEffect, useState } from 'preact/hooks';
import {
  addChatGrant,
  listChatGrants,
  removeChatGrant,
  type ChatGrant,
  type TeamMember,
} from '../lib/api';
import { describeReadFailure } from '../lib/readFailure';
import './ChatPeople.css';

// CV2 §4 — WHO IS IN THIS CONVERSATION, and the office's hand on the door.
//
// A chat grant is the office's explicit "add them in even if they weren't
// assigned" (CV1 §1): it only ever ADDS to the derived set, and revoking it
// removes the grant, never an assignment. This panel is therefore honest
// about its own scope — it lists GRANTS, and says in one line that
// assignment reaches the thread on its own, because a panel that showed
// three granted names for a thread five assigned techs can read would
// otherwise be lying by omission.
//
// WHO GETS THE DOOR: the screen that mounts this gates it on the team
// roster having actually answered (admin-gated server-side) — the S5d
// enrolment-door posture: no dead-end buttons, and no client-side copy of
// the role predicate. The grants read itself is manager+ (the same
// laneVisibleTo(job) gate as the POST); a refusal renders as a refusal.

export interface ChatPeopleProps {
  tenantId: string;
  recordId: string;
  /** The admin-only roster, already read by the mounting screen. */
  roster: TeamMember[];
}

export function ChatPeople({ tenantId, recordId, roster }: ChatPeopleProps) {
  const [open, setOpen] = useState(false);
  const [grants, setGrants] = useState<ChatGrant[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFailure(null);
    listChatGrants(tenantId, recordId)
      .then((rows) => {
        if (!cancelled) setGrants(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        const said = describeReadFailure(err);
        setFailure(`${said.headline} ${said.detail}`);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, recordId]);

  function nameOf(membershipId: string): string {
    const m = roster.find((r) => r.id === membershipId);
    return m?.name ?? m?.email ?? membershipId;
  }

  async function add(membershipId: string) {
    if (busy !== null) return;
    setBusy(membershipId);
    setFailure(null);
    try {
      const grant = await addChatGrant(tenantId, { recordId, membershipId });
      setGrants((prev) => {
        const rest = (prev ?? []).filter((g) => g.membershipId !== membershipId);
        return [...rest, grant];
      });
    } catch (err) {
      const said = describeReadFailure(err);
      setFailure(`${said.headline} ${said.detail}`);
    }
    setBusy(null);
  }

  async function remove(membershipId: string) {
    if (busy !== null) return;
    setBusy(membershipId);
    setFailure(null);
    try {
      await removeChatGrant(tenantId, { recordId, membershipId });
      setGrants((prev) => (prev ?? []).filter((g) => g.membershipId !== membershipId));
    } catch (err) {
      const said = describeReadFailure(err);
      setFailure(`${said.headline} ${said.detail}`);
    }
    setBusy(null);
  }

  const grantedIds = new Set((grants ?? []).map((g) => g.membershipId));
  const addable = roster.filter((m) => m.status === 'active' && !grantedIds.has(m.id));

  return (
    <div class="chat-people">
      <button
        type="button"
        class="chat-people__door"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="people-door"
      >
        People
      </button>

      {open ? (
        <div class="chat-people__panel" data-testid="people-panel">
          {failure !== null ? (
            <div class="chat-people__say" role="status">
              {failure}
            </div>
          ) : grants === null ? (
            <div class="chat-people__say">One moment…</div>
          ) : (
            <>
              <div class="chat-people__sec">Added by the office</div>
              {grants.length === 0 ? (
                <div class="chat-people__say">
                  Nobody has been added. Whoever the job is assigned to reaches this thread on
                  their own.
                </div>
              ) : (
                grants.map((g) => (
                  <div class="chat-people__row" key={g.membershipId}>
                    <span class="chat-people__name">{nameOf(g.membershipId)}</span>
                    <button
                      type="button"
                      class="chat-people__act"
                      disabled={busy !== null}
                      onClick={() => void remove(g.membershipId)}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
              {addable.length > 0 ? (
                <>
                  <div class="chat-people__sec">Add someone</div>
                  {addable.map((m) => (
                    <div class="chat-people__row" key={m.id}>
                      <span class="chat-people__name">{m.name ?? m.email ?? m.id}</span>
                      <button
                        type="button"
                        class="chat-people__act"
                        disabled={busy !== null}
                        onClick={() => void add(m.id)}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </>
              ) : null}
              <div class="chat-people__fine">
                Adding someone opens this job’s conversation to them. Removing them closes only
                what was added — never an assignment.
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
